import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { Refresh, ZoomIn, ZoomOut, Close, CloudUpload } from '@mui/icons-material';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

interface Spritesheet {
  className: string;
  url: string;
  exists: boolean;
  dimensions: {
    width: number;
    height: number;
  };
}

interface SpritesheetViewerProps {
  subreddit: string;
  /** Mods of the subreddit and site admins can regenerate/push spritesheets. */
  canManageSpritesheets?: boolean;
  /** Subscribed subs use parent CDN assets; local generate is disabled but Reddit push remains. */
  isSubscribed?: boolean;
}

export const SpritesheetViewer: React.FC<SpritesheetViewerProps> = ({
  subreddit,
  canManageSpritesheets = false,
  isSubscribed = false,
}) => {
  const { user } = useAuth();
  const [spritesheets, setSpritesheets] = useState<Spritesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState<string | null>(null);
  const [pushing, setPushing] = useState<string | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);
  const [selectedSpritesheet, setSelectedSpritesheet] = useState<Spritesheet | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pushingEmojis, setPushingEmojis] = useState<string | null>(null);
  const [pushEmojiError, setPushEmojiError] = useState<string | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{
    current: number;
    total: number;
    detail: string;
  } | null>(null);

  useEffect(() => {
    loadSpritesheets();
  }, [subreddit]);

  const loadSpritesheets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/spritesheets/${subreddit}`);
      
      if (!response.ok) {
        throw new Error('Failed to load spritesheets');
      }
      
      const data = await response.json();
      setSpritesheets(data.spritesheets || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load spritesheets');
    } finally {
      setLoading(false);
    }
  };

  const requestGenerateSpritesheet = async (className: string) => {
    if (!user || !user.username) {
      throw new Error('Not authenticated');
    }
    const response = await fetch(
      `${API_BASE_URL}/moderator/${subreddit}/spritesheets/${className}/generate`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user.username}`,
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { error?: string }).error || 'Failed to generate spritesheet'
      );
    }
  };

  const requestPushSpritesheet = async (className: string) => {
    if (!user || !user.username) {
      throw new Error('Not authenticated');
    }
    const response = await fetch(
      `${API_BASE_URL}/moderator/${subreddit}/spritesheets/${className}/push-spritesheet`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user.username}`,
        },
      }
    );
    if (!response.ok) {
      const responseText = await response.text();
      let errorData: { error?: string; details?: string };
      try {
        errorData = JSON.parse(responseText);
      } catch {
        errorData = {
          error: `HTTP ${response.status}: ${response.statusText}`,
          details: responseText.substring(0, 500),
        };
      }
      const errorMsg =
        errorData.error || errorData.details || `Failed to push spritesheet (${response.status})`;
      throw new Error(errorMsg);
    }
    const data = await response.json();
    if (data.success === false) {
      throw new Error(data.error || data.details || 'Push spritesheet failed');
    }
  };

  const runBulkPushAll = async () => {
    if (!canManageSpritesheets || !user?.username || spritesheets.length === 0) return;
    setBulkRunning(true);
    setError(null);
    setPushError(null);
    const total = spritesheets.length;
    try {
      let step = 0;
      for (const s of spritesheets) {
        step += 1;
        setBulkProgress({
          current: step,
          total,
          detail: `Pushing ${s.className} to Reddit…`,
        });
        await requestPushSpritesheet(s.className);
      }
      await loadSpritesheets();
      alert('All spritesheets were pushed to Reddit.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Bulk push failed';
      setError(msg);
    } finally {
      setBulkRunning(false);
      setBulkProgress(null);
    }
  };

  const runBulkGenerateThenPush = async () => {
    if (!canManageSpritesheets || !user?.username || spritesheets.length === 0) return;
    if (isSubscribed) {
      await runBulkPushAll();
      return;
    }
    setBulkRunning(true);
    setError(null);
    setPushError(null);
    const total = spritesheets.length * 2;
    let step = 0;
    try {
      for (const s of spritesheets) {
        step += 1;
        setBulkProgress({
          current: step,
          total,
          detail: `Generating ${s.className}…`,
        });
        await requestGenerateSpritesheet(s.className);
        step += 1;
        setBulkProgress({
          current: step,
          total,
          detail: `Pushing ${s.className} to Reddit…`,
        });
        await requestPushSpritesheet(s.className);
      }
      await loadSpritesheets();
      alert('All spritesheets were generated and pushed to Reddit.');
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Bulk generate/push failed';
      setError(msg);
    } finally {
      setBulkRunning(false);
      setBulkProgress(null);
    }
  };

  const generateSpritesheet = async (className: string) => {
    try {
      setGenerating(className);
      setError(null);

      await requestGenerateSpritesheet(className);

      // Reload spritesheets with cache bust
      await loadSpritesheets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate spritesheet');
    } finally {
      setGenerating(null);
    }
  };

  const handleViewSpritesheet = (spritesheet: Spritesheet) => {
    if (spritesheet.exists) {
      setSelectedSpritesheet(spritesheet);
      setZoom(1);
    }
  };

  const handleCloseDialog = () => {
    setSelectedSpritesheet(null);
    setZoom(1);
  };

  const pushSpritesheetToReddit = async (className: string) => {
    if (!user || !user.username) {
      setPushError('Not authenticated');
      return;
    }

    setPushing(className);
    setPushError(null);

    try {
      await requestPushSpritesheet(className);
      alert(`Successfully pushed spritesheet to Reddit!`);
    } catch (err) {
      console.error('Push spritesheet exception:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to push spritesheet';
      setPushError(errorMessage);
    } finally {
      setPushing(null);
    }
  };

  const pushEmojisToReddit = async (className: string) => {
    if (!user || !user.username) {
      setPushEmojiError('Not authenticated');
      return;
    }

    setPushingEmojis(className);
    setPushEmojiError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/moderator/${subreddit}/spritesheets/${className}/push-emojis`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${user.username}`,
          },
        }
      );

      if (!response.ok) {
        let errorData;
        const responseText = await response.text();
        console.error(`Push emojis HTTP ${response.status}:`, responseText);
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { 
            error: `HTTP ${response.status}: ${response.statusText}`,
            details: responseText.substring(0, 500)
          };
        }
        const errorMsg = errorData.error || errorData.details || `Failed to push emojis (${response.status})`;
        throw new Error(errorMsg);
      }

      const data = await response.json();
      if (data.success === false) {
        throw new Error(data.error || data.details || 'Push emojis failed');
      }
      
      // The endpoint runs in background, so we show a message that it started
      alert(`Emoji push started for ${className}. Processing in background - this may take several minutes. Check server logs for progress.`);
    } catch (err) {
      console.error('Push emojis exception:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to push emojis';
      setPushEmojiError(errorMessage);
    } finally {
      setPushingEmojis(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h5">Spritesheets for {subreddit}</Typography>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {canManageSpritesheets && (
            <Button
              variant="contained"
              color="primary"
              disabled={bulkRunning || spritesheets.length === 0}
              onClick={runBulkGenerateThenPush}
            >
              {bulkRunning ? 'Working…' : isSubscribed ? 'Push all to Reddit' : 'Generate & push all'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadSpritesheets}
            disabled={bulkRunning}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {isSubscribed && canManageSpritesheets && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This sub subscribes to another community for flair data. Spritesheet PNGs are not generated here, but you can
          still push spritesheets and emojis to <strong>/r/{subreddit}</strong> on Reddit using assets from the CDN.
        </Alert>
      )}

      {bulkRunning && bulkProgress && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={(100 * bulkProgress.current) / bulkProgress.total}
            sx={{ mb: 0.5 }}
          />
          <Typography variant="body2" color="text.secondary">
            {bulkProgress.detail} ({bulkProgress.current}/{bulkProgress.total})
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {pushError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPushError(null)}>
          {pushError}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {spritesheets.map((spritesheet) => (
          <Box key={spritesheet.className}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Typography variant="h6">{spritesheet.className}</Typography>
                  {spritesheet.exists ? (
                    <Chip label="Generated" color="success" size="small" />
                  ) : (
                    <Chip label="Not Generated" color="warning" size="small" />
                  )}
                </Box>
                
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {spritesheet.dimensions.width}x{spritesheet.dimensions.height}px
                </Typography>

                {spritesheet.exists ? (
                  <Box>
                    <Box sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                      <img 
                        src={spritesheet.url} 
                        alt={`${spritesheet.className} spritesheet`}
                        style={{ width: '100%', height: 'auto', display: 'block', cursor: 'pointer' }}
                        onClick={() => handleViewSpritesheet(spritesheet)}
                      />
                    </Box>
                    {canManageSpritesheets && (
                      <>
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<CloudUpload />}
                          onClick={() => pushSpritesheetToReddit(spritesheet.className)}
                          disabled={bulkRunning || pushing === spritesheet.className}
                          sx={{ mb: 1 }}
                        >
                          {pushing === spritesheet.className ? 'Pushing...' : 'Push Spritesheet'}
                        </Button>
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<CloudUpload />}
                          onClick={() => pushEmojisToReddit(spritesheet.className)}
                          disabled={bulkRunning || pushingEmojis === spritesheet.className}
                          sx={{ mb: 1 }}
                        >
                          {pushingEmojis === spritesheet.className ? 'Pushing...' : 'Push Emojis'}
                        </Button>
                        {pushError && (
                          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                            {pushError}
                          </Alert>
                        )}
                        {pushEmojiError && (
                          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                            {pushEmojiError}
                          </Alert>
                        )}
                        {!isSubscribed && (
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<Refresh />}
                            onClick={() => generateSpritesheet(spritesheet.className)}
                            disabled={bulkRunning || generating === spritesheet.className}
                          >
                            {generating === spritesheet.className ? 'Generating...' : 'Regenerate'}
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                ) : (
                  <>
                    {canManageSpritesheets && !isSubscribed && (
                      <Button
                        variant="contained"
                        fullWidth
                        color="primary"
                        onClick={() => generateSpritesheet(spritesheet.className)}
                        disabled={bulkRunning || generating === spritesheet.className}
                      >
                        {generating === spritesheet.className ? (
                          <>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Generating...
                          </>
                        ) : (
                          'Generate Spritesheet'
                        )}
                      </Button>
                    )}
                    {isSubscribed && (
                      <Typography variant="body2" color="text.secondary">
                        Spritesheets are produced on the parent sub; after nightly sync, refresh above. Use Push when the
                        CDN file exists.
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Spritesheet Viewer Dialog */}
      <Dialog
        open={!!selectedSpritesheet}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedSpritesheet?.className} Spritesheet
            </Typography>
            <Box>
              <IconButton
                onClick={() => setZoom(Math.max(0.25, zoom - 0.25))}
                disabled={zoom <= 0.25}
              >
                <ZoomOut />
              </IconButton>
              <IconButton
                onClick={() => setZoom(Math.min(3, zoom + 0.25))}
                disabled={zoom >= 3}
              >
                <ZoomIn />
              </IconButton>
              <IconButton onClick={handleCloseDialog}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSpritesheet && (
            <Box
              sx={{
                overflow: 'auto',
                maxHeight: '70vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'start',
                backgroundColor: '#f5f5f5',
                p: 2,
              }}
            >
              <Box
                component="img"
                src={selectedSpritesheet.url}
                alt={`${selectedSpritesheet.className} spritesheet`}
                sx={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top left',
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            Zoom: {Math.round(zoom * 100)}%
          </Typography>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpritesheetViewer;
